import { LOADING_TIP_READ_MS, LOADING_TIPS } from '@sorrem-client/game/loading-tips';
import type { UILoadingTip } from '@sorrem-client/types';
import { useCallback, useEffect, useState } from 'react';

export interface LoadingTipState {
    readonly tip: UILoadingTip;
    /** 1-based, for "3 of 25". */
    readonly position: number;
    readonly count: number;
    showNext(): void;
    showPrevious(): void;
}

/**
 * The tip shown last, kept across loading screens: the way into a crucible
 * passes through several, and the tip shouldn't jump each time one gives
 * way to the next. Null until the first loading screen picks one at random.
 */
let currentIndex: number | null = null;

function wrap(index: number): number {
    return (index + LOADING_TIPS.length) % LOADING_TIPS.length;
}

/** The loading screen's tip, and stepping through the catalog either way. */
export function useLoadingTip(): LoadingTipState {
    // Not SeededRng: which tip shows first is presentation, never game state.
    const [index, setIndex] = useState(() => currentIndex ?? Math.floor(Math.random() * LOADING_TIPS.length));
    currentIndex = index;

    const showNext = useCallback(() => setIndex((current) => wrap(current + 1)), []);
    const showPrevious = useCallback(() => setIndex((current) => wrap(current - 1)), []);

    return { tip: LOADING_TIPS[index]!, position: index + 1, count: LOADING_TIPS.length, showNext, showPrevious };
}

/**
 * Whether a loading screen shown since this hook mounted has stayed up long
 * enough to read a tip (`LOADING_TIP_READ_MS`), however fast the load
 * behind it finishes.
 */
export function useIsLoadingTipRead(): boolean {
    const [isTipRead, setIsTipRead] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setIsTipRead(true), LOADING_TIP_READ_MS);
        return (): void => clearTimeout(timer);
    }, []);
    return isTipRead;
}
